import React from 'react';
import PropTypes from 'prop-types';
import {Set, List} from 'immutable';
import ImmutablePropTypes from 'react-immutable-proptypes';
import {Link} from 'react-router';
import {SortableContainerProvider, addToast} from '@albert-io/atomic';
import {queryBuilderPropType} from '@albert-io/json-api-framework/request/builder';
import PracticeView from 'components/PracticeView/PracticeView.react';
import QuestionActions from 'components/PracticeView/QuestionActions/QuestionActions.react';
import HeadingActions from 'components/PracticeView/HeadingActions/HeadingActions.react';
import {hasEditPermissions, hasMoreQuestionInfo} from 'components/PracticeView/practiceView.utils';
import TitleBar from 'components/PracticeView/TitleBar/TitleBar.react';
import QuestionsListPaginator from 'components/PracticeView/QuestionsListPaginator/QuestionsListPaginator.react';
import QuestionsListHeader from 'components/PracticeView/QuestionsListHeader/QuestionsListHeader.react';
import LoadingView from 'components/PracticeView/LoadingView/LoadingView.react';
import {pluralize} from 'lib/stringUtils';
import {invalidatePartialInterest, genericMandarkRequest} from 'resources/mandark.resource';
import {getQuestionEditPath} from 'client/Dennis/Content/Queue/shared';
import masqueradeStore from 'generic/Masquerade/Masquerade.store';
import {getModelForResourceType} from 'resources/modelRegistry';
import {
  getPreviousQuestionState,
  getNextQuestionState
} from 'components/PracticeView/helpers/questionNavigation.utils';
import PaginatedResultsManager from 'components/PracticeView/helpers/paginatedResultsManager';
import makeTimeElapsedManager from 'components/PracticeView/helpers/makeTimeElapsedManager';
import {destroyQuestionStoresForQuestionSets} from 'client/components/QuestionTypes/questionTypes.utils';
import {MobilePracticeViewProvider} from 'components/PracticeView/MobilePracticeViewProvider.context';
import {getActiveSetAndQuestion} from 'client/SubjectPracticeView/subjectPracticeView.utils';

import GuideLevelToolbar from './GuideLevelToolbar.react';
import {
  getGuideLevelQuestionSetsQuery,
  getGuideLevelQuestionSets,
  getGuideLevelDuplicateSets,
  getAuthoringSubjectGuideLevelBySlug,
  getAuthoringSubjectBySlug
} from './GuideLevelPracticeView.queries';

import './guide-level-practice-view.scss';

class BootstrappedGuideLevelView extends React.Component {
  static propTypes = {
    guideLevel: PropTypes.instanceOf(getModelForResourceType('guide_level_v2')),
    subject: PropTypes.instanceOf(getModelForResourceType('subject_v2')),
    questionSets: PropTypes.instanceOf(List),
    questionSetsQuery: queryBuilderPropType,
    questionCount: PropTypes.number,
    questionSetsCount: PropTypes.number,
    isLoading: PropTypes.bool,
    location: PropTypes.object,
    duplicateQuestionSetIds: ImmutablePropTypes.list
  };

  constructor(props) {
    super(props);
    const {
      activeQuestionSet,
      activeQuestion,
      selectedQuestionSets,
      paginatedQuestionsListManager,
      totalQuestions
    } = this.getBootstrapValues();
    this.state = {
      activeQuestionSet,
      activeQuestion,
      totalQuestions,
      isManualSort: false,
      selectedQuestionSets,
      activeGuess: null,
      showFolderModal: false,
      isSelectAllQuestionSetsMode: false,
      isSavingReorder: false,
      hasSavedReorder: false
    };
    this.paginatedQuestionsListManager = paginatedQuestionsListManager;
    this.timeElapsedManager = makeTimeElapsedManager(activeQuestion);
    this.scrollToQuestionById = React.createRef();

    this.timeoutId = null;
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.questionSetsQuery.equals(this.props.questionSetsQuery)) {
      const {
        activeQuestionSet,
        activeQuestion,
        selectedQuestionSets,
        activeGuess,
        paginatedQuestionsListManager,
        totalQuestions
      } = this.getBootstrapValues();
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        activeQuestionSet,
        activeQuestion,
        selectedQuestionSets,
        activeGuess,
        totalQuestions
      });
      this.paginatedQuestionsListManager = paginatedQuestionsListManager;
    }
  }

  componentWillUnmount() {
    destroyQuestionStoresForQuestionSets(this.props.questionSets);
    clearTimeout(this.timeoutId);
  }

  getBootstrapValues() {
    const activeSetAndQuestion = getActiveSetAndQuestion(this.props);
    const totalQuestions = this.props.questionSetsQuery
      .getResourceMetadata()
      .getIn(['request', 'count_of_questions'], null);
    return {
      activeQuestionSet: activeSetAndQuestion.activeQuestionSet,
      activeQuestion: activeSetAndQuestion.activeQuestion,
      selectedQuestionSets: Set(),
      paginatedQuestionsListManager: this.getNewResultsManager(),
      totalQuestions
    };
  }

  getNewResultsManager = () =>
    new PaginatedResultsManager({
      initialQuery: this.props.questionSetsQuery,
      onUpdate: (incomingQuestionSets) => {
        if (!incomingQuestionSets) {
          this.forceUpdate();
        }
      }
    });

  toggleFoldersModal = () => {
    if (this.state.showFolderModal) {
      this.setState({
        showFolderModal: false
      });
    } else {
      this.setState({
        showFolderModal: true
      });
    }
  };

  makeQuestionSetOnChangeHandler = (questionSet) => {
    return () => {
      const questionSetId = questionSet.getId();
      this.setState((state) => {
        const newSelectedSets = state.selectedQuestionSets.includes(questionSetId)
          ? state.selectedQuestionSets.delete(questionSetId)
          : state.selectedQuestionSets.add(questionSetId);

        const isDeselectingLastSelectAllSet =
          state.isSelectAllQuestionSetsMode &&
          newSelectedSets.size === this.props.questionSetsCount;

        return isDeselectingLastSelectAllSet
          ? {
              selectedQuestionSets: Set(),
              isSelectAllQuestionSetsMode: false
            }
          : {
              selectedQuestionSets: newSelectedSets
            };
      });
    };
  };

  setActiveGuess = (guess) => {
    this.setState({activeGuess: guess});
  };

  makeNavigateProps(upcomingSetAndQuestion) {
    if (!upcomingSetAndQuestion) {
      return {disabled: true};
    }
    const {questionSet, question} = upcomingSetAndQuestion;
    return {
      disabled: false,
      handleNavigation: () => {
        this.scrollToQuestionById.current(questionSet.getId());
        this.navigateToQuestion(questionSet, question);
      }
    };
  }

  makeNavigateBackProps = () => {
    const questionSets = this.paginatedQuestionsListManager.getResults();
    if (questionSets.isEmpty()) {
      return null;
    }
    const previousSetAndQuestion = getPreviousQuestionState({
      questionSets,
      activeSet: this.state.activeQuestionSet,
      activeQuestion: this.state.activeQuestion
    });
    return this.makeNavigateProps(previousSetAndQuestion);
  };

  makeNavigateNextProps = () => {
    const questionSets = this.paginatedQuestionsListManager.getResults();
    if (questionSets.isEmpty()) {
      return null;
    }
    const nextSetAndQuestion = getNextQuestionState({
      questionSets,
      activeSet: this.state.activeQuestionSet,
      activeQuestion: this.state.activeQuestion
    });
    return this.makeNavigateProps(nextSetAndQuestion);
  };

  navigateToQuestion(questionSet, question) {
    return new Promise((res) => {
      this.setState(
        {
          activeQuestionSet: questionSet,
          activeQuestion: question,
          activeGuess: null
        },
        () => {
          res();
        }
      );
    });
  }

  // from FPV
  setActiveQuestionState = (activeQuestion, activeQuestionSet) => {
    this.setState({
      activeQuestion,
      activeQuestionSet
    });
  };

  // Taken directly from SubjectPracticeView
  navigateAndScrollToPage(pageNum) {
    const firstSetOnPage = this.paginatedQuestionsListManager
      .getQuestionSetsInPage(pageNum)
      .first();
    const firstQuestion = firstSetOnPage.getQuestions().first();
    this.setActiveQuestionState(firstQuestion, firstSetOnPage);
    this.scrollToQuestionById.current(firstSetOnPage.getId());
  }

  handlePaginatorPreviousClick = async () => {
    const previousPage =
      this.paginatedQuestionsListManager.getPageForId(this.state.activeQuestionSet.getId()) - 1;
    if (!this.paginatedQuestionsListManager.hasFetchedPage(previousPage)) {
      await this.paginatedQuestionsListManager.fetchPreviousPage();
    }
    this.navigateAndScrollToPage(previousPage);
  };

  handlePaginatorNextClick = async () => {
    const nextPage =
      this.paginatedQuestionsListManager.getPageForId(this.state.activeQuestionSet.getId()) + 1;
    if (!this.paginatedQuestionsListManager.hasFetchedPage(nextPage)) {
      await this.paginatedQuestionsListManager.fetchNextPage();
    }
    this.navigateAndScrollToPage(nextPage);
  };

  clearQuestionSetSelections = () => {
    this.setState({
      selectedQuestionSets: Set(),
      isSelectAllQuestionSetsMode: false
    });
  };

  getHeadingActions() {
    const {activeQuestion, activeQuestionSet} = this.state;
    const {subject} = this.props;
    if (!activeQuestion) {
      return null;
    }

    const subjectId = subject.getId();
    const questionSetId = activeQuestionSet.getId();
    return (
      <HeadingActions>
        <HeadingActions.EditQuestionItem
          key='EditQuestionItem'
          editPath={getQuestionEditPath({
            questionSetId: activeQuestionSet.getId(),
            questionId: activeQuestion.getId(),
            customSearch: {
              status: 'published',
              subject: subjectId,
              page: questionSetId,
              fallback_page: 1
            }
          })}
        />
      </HeadingActions>
    );
  }

  // taken from FPV
  getFooterActions() {
    const {activeQuestion, activeGuess, activeQuestionSet} = this.state;

    const actions = [];
    let key = 0;
    actions.push(
      <QuestionActions.ToggleViewSolution
        key={(key += 1)}
        question={activeQuestion}
        isViewingSolution={!!activeGuess}
        handleToggleSolution={this.setActiveGuess}
      />
    );
    if (hasMoreQuestionInfo()) {
      actions.push(
        <QuestionActions.MoreInfo
          key={(key += 1)}
          questionSetId={activeQuestionSet.getId()}
          userId={masqueradeStore.getUserIdByMasqueradeState()}
        />
      );
    }
    if (hasEditPermissions(activeQuestion)) {
      actions.push(
        <QuestionActions.EditQuestionLink
          key={(key += 1)}
          question={activeQuestion}
          questionSet={activeQuestionSet}
        />
      );
    }
    return (
      <QuestionActions questionStoreName={activeQuestion.getStore().getName()}>
        {actions}
      </QuestionActions>
    );
  }

  setManualSort = (val) => {
    this.setState({isManualSort: val});
  };

  toggleSelectAllQuestionSets = (e) => {
    this.setState({
      isSelectAllQuestionSetsMode: e.target.checked,
      selectedQuestionSets: Set()
    });
  };

  isQuestionSetSelected = (questionSet) => {
    const {selectedQuestionSets, isSelectAllQuestionSetsMode} = this.state;
    const isIncluded = selectedQuestionSets.includes(questionSet.getId());
    return isSelectAllQuestionSetsMode ? !isIncluded : isIncluded;
  };

  getSelectedQuestionCount() {
    const {selectedQuestionSets, isSelectAllQuestionSetsMode} = this.state;
    const selectedCount = selectedQuestionSets.reduce((total, id) => {
      const questionSet = this.paginatedQuestionsListManager.getQuestionSetById(id);
      const questionCount = questionSet.getQuestions().size;
      return total + questionCount;
    }, 0);
    return isSelectAllQuestionSetsMode ? this.props.questionCount - selectedCount : selectedCount;
  }

  // gets positions of the selected question sets
  getSelectedSetIndices = () => {
    const questionSets = this.paginatedQuestionsListManager.getResults();
    const indices = this.state.selectedQuestionSets.reduce((arr, selectedId) => {
      const index = questionSets.findIndex((set) => set.getId() === selectedId);
      return index >= 0 ? arr.add(index) : arr;
    }, Set());
    return indices.sort();
  };

  // For SortableContainer
  onSortEnd = (questionSetIndices, initialIndex, newIndex, shouldCancel) => {
    // This logic is odd because we have no notion of `bulk move` outside of the sortable container
    // if `bulk move` we can allow the selected indices to have the destination index (newIndex)
    if (!this.state.isManualSort && questionSetIndices) {
      this.paginatedQuestionsListManager.moveQuestionSetToIndex(questionSetIndices, newIndex);
      this.saveReordering();
    } else if (this.state.isManualSort && !shouldCancel) {
      this.paginatedQuestionsListManager.moveQuestionSetToIndex(questionSetIndices, newIndex);
      this.saveReordering();
    } else {
      // we don't want to invalidate the resource here since we are not saving any changes yet
      this.forceUpdate();
    }
    this.setManualSort(false);
  };

  cancelRearranging = ({cancelSortMode = () => {}, setHasSorted = () => {}}) => {
    cancelSortMode();
    setHasSorted(false);
    this.setManualSort(false);
    this.invalidateQuestionsList();
  };

  /**
   * This function handles the saving and refreshing of the questions list after a reorder operation.
   *
   * @param {func} setHasSorted : function to set state in sort provider
   * @param {bool} moveToEnd : Bool for setting sets to the end. This is needed to handle operations with pagination and to trigger a list refresh
   */
  saveReordering = async (setHasSorted, moveToEnd = false) => {
    const questionSets = this.paginatedQuestionsListManager.getResults();
    // reduce lists to arrays of question set IDs
    const questionSetIds = moveToEnd
      ? this.getSelectedSetIndices()
          .sort()
          .reduce((arr, index) => {
            arr.push(questionSets.get(index).getId());
            return arr;
          }, [])
      : this.paginatedQuestionsListManager.getResults().reduce((acc, questionSet) => {
          acc.push(questionSet.getId());
          return acc;
        }, []);
    const payload = {
      data: {
        attributes: {
          reorder_question_sets: {
            order: questionSetIds,
            reorder_at_end: moveToEnd
          }
        }
      }
    };
    this.setState({
      isSavingReorder: true
    });
    try {
      await genericMandarkRequest(
        'patch',
        {
          resourcePath: ['authoring_guide_levels_v1', this.props.guideLevel.getId()]
        },
        payload
      );
      // Design requested to artifically delay action this to allow for a timer to be shown.
      // Since this is only for content we know that they will be using fast computers so this _should_ be okay
      this.timeoutId = setTimeout(
        () =>
          this.setState({
            isSavingReorder: false,
            hasSavedReorder: true
          }),
        500
      );
      this.invalidateQuestionsList();
      if (moveToEnd) {
        // resets pagination - PV is not built to fetch several pages at once, if this is an issue we can think about fixing
        this.paginatedQuestionsListManager.maxPage = 1;
        this.repopulateQuestionsList();
        if (this.state.totalQuestions > this.paginatedQuestionsListManager.pageSize) {
          this.clearQuestionSetSelections();
        }
      }
    } catch (e) {
      addToast({
        type: 'danger',
        title: 'Error',
        message: 'Something went wrong with saving your changes.'
      });
    } finally {
      this.cancelRearranging({setHasSorted});
    }
  };

  // For sorting
  invalidateQuestionsList = () => {
    invalidatePartialInterest({resourcePath: this.props.questionSetsQuery.done().resourcePath});
  };

  /*
   * We use this function when the questions list is out of sync.
   * Simply invalidating the resource doesn't work since we need to repopulate the paginatedManager and its sets
   *
   * @todo See if there is a cleaner way to handle this data flow through the wrapper component.
   */
  repopulateQuestionsList = async () => {
    try {
      const questionSets = await getGuideLevelQuestionSets(this.props.guideLevel.getId());
      this.paginatedQuestionsListManager.questionSetsMap =
        this.paginatedQuestionsListManager.convertSetsToOrderedMap(questionSets);
      // due to pagination, we reset the active question in case the question was moved out of view
      // @todo: Could probably cross check with the list of selected sets and only reset if the active set was moved
      this.setState({
        activeQuestion: questionSets.first().getQuestions().first(),
        activeQuestionSet: questionSets.first()
      });
    } catch (e) {
      addToast({
        type: 'danger',
        title: 'Error',
        message: 'Something went wrong with loading the new questions. Please refresh your page.'
      });
    }
  };

  render() {
    const {
      guideLevel,
      isLoading,
      subject,
      questionCount,
      location,
      questionSetsCount,
      duplicateQuestionSetIds
    } = this.props;
    const {
      activeQuestion,
      activeQuestionSet,
      activeGuess,
      isSelectAllQuestionSetsMode,
      selectedQuestionSets,
      isSavingReorder,
      hasSavedReorder,
      isManualSort,
      totalQuestions
    } = this.state;

    const titlebarBackButton = (
      <TitleBar.BackButton
        as={Link}
        label='Back To Dennis'
        to={location.state?.from || `/dennis/content/subjects/${subject.getId()}/guides/practice`}
      />
    );

    if (!activeQuestion) {
      return (
        <PracticeView
          toolbar={<GuideLevelToolbar location={location} isEmptyPracticeView />}
          titleBar={<TitleBar title={guideLevel.getName()} backButton={titlebarBackButton} />}
          questionsListProps={{
            header: <QuestionsListHeader heading='No questions found' />,
            noQuestionSetsMessage: 'Your guide has no published or hidden question sets.'
          }}
        />
      );
    }

    const selectedQuestionCount = this.getSelectedQuestionCount();
    const questionSets = this.paginatedQuestionsListManager.getResults();

    const activeSubject = subject;
    const toolbar = (
      <GuideLevelToolbar
        selectedQuestionCount={selectedQuestionCount}
        questionCount={questionCount}
        location={location}
        questionSets={questionSets}
        selectedQuestionSets={selectedQuestionSets}
        cancelRearranging={this.cancelRearranging}
        saveReordering={this.saveReordering}
        clearQuestionSetSelections={this.clearQuestionSetSelections}
        isSavingReorder={isSavingReorder}
        hasSavedReorder={hasSavedReorder}
        isSelectAllQuestionSetsMode={isSelectAllQuestionSetsMode}
        questionSetsCount={questionSetsCount}
        getSelectedSetIndices={this.getSelectedSetIndices}
        setManualSort={this.setManualSort}
        isManualSort={isManualSort}
        duplicateQuestionSetIds={duplicateQuestionSetIds}
      />
    );

    const navigateBackProps = this.makeNavigateBackProps();
    const navigateNextProps = this.makeNavigateNextProps();
    return (
      <SortableContainerProvider onSortEnd={this.onSortEnd}>
        <MobilePracticeViewProvider>
          <PracticeView
            activeQuestion={activeQuestion}
            activeQuestionSet={activeQuestionSet}
            activeQuestionStore={activeQuestion && activeQuestion.getStore()}
            activeGuess={activeGuess}
            activeSubject={activeSubject}
            questionSets={questionSets}
            isLoading={isLoading}
            toolbar={toolbar}
            // Always have access in Dennis
            hasQuestionSetAccessPredicate={() => true}
            titleBar={
              <TitleBar
                backgroundColor={activeSubject.getCurriculumArea().getColor()}
                title={guideLevel.getName()}
                imgHref={activeSubject.getLogoLocation().href}
                backButton={titlebarBackButton}
              />
            }
            questionsListProps={{
              onScrollLastQuestionIntoView: () => {
                if (!this.paginatedQuestionsListManager.isRequestInFlight) {
                  this.paginatedQuestionsListManager.fetchNextPage();
                }
              },
              scrollToQuestionById: this.scrollToQuestionById,
              header: (
                <QuestionsListHeader
                  checkboxProps={
                    !isManualSort &&
                    duplicateQuestionSetIds.isEmpty() && {
                      checked:
                        isSelectAllQuestionSetsMode || selectedQuestionCount === questionCount,
                      onChange: this.toggleSelectAllQuestionSets,
                      indeterminate:
                        selectedQuestionCount > 0 && selectedQuestionCount < questionCount
                    }
                  }
                  heading={
                    totalQuestions !== null
                      ? `${totalQuestions} ${pluralize('question', totalQuestions)}`
                      : null
                  }
                  selectedQuestionCount={this.getSelectedQuestionCount()}
                />
              ),
              footer: (() => {
                const currentPage = this.paginatedQuestionsListManager.getPageForId(
                  activeQuestionSet.getId()
                );
                return (
                  <div>
                    <QuestionsListPaginator
                      currentPage={currentPage}
                      isRequestInFlight={this.paginatedQuestionsListManager.isRequestInFlight}
                      totalPages={this.paginatedQuestionsListManager.totalPages}
                      isPreviousDisabled={
                        this.paginatedQuestionsListManager.isRequestInFlight || currentPage === 1
                      }
                      handlePreviousPage={this.handlePaginatorPreviousClick}
                      isNextDisabled={
                        this.paginatedQuestionsListManager.isRequestInFlight ||
                        currentPage === this.paginatedQuestionsListManager.totalPages
                      }
                      handleNextPage={this.handlePaginatorNextClick}
                    />
                  </div>
                );
              })()
            }}
            questionSetProps={{
              selectable: () => !isManualSort && duplicateQuestionSetIds.isEmpty(),
              rearrangeable: isManualSort,
              isSelected: this.isQuestionSetSelected,
              selectedQuestionSets: this.state.selectedQuestionSets,
              questionSetOnChangeHandler: this.makeQuestionSetOnChangeHandler,
              isSelectAllQuestionSetsMode: this.state.isSelectAllQuestionSetsMode,
              duplicateQuestionSetIds
            }}
            questionProps={{
              onQuestionClick: ({question, questionSet}) => {
                return () => {
                  this.setState({
                    activeQuestion: question,
                    activeQuestionSet: questionSet,
                    activeGuess: null
                  });
                };
              }
            }}
            questionRendererProps={{
              actions: this.getFooterActions(),
              rendererType: activeGuess ? 'PostGuess' : 'PreGuess',
              navigateBackProps,
              navigateNextProps,
              headingActions: this.getHeadingActions()
            }}
          />
        </MobilePracticeViewProvider>
      </SortableContainerProvider>
    );
  }
}

export default class GuideLevelPracticeView extends React.Component {
  static propTypes = {
    params: PropTypes.object,
    location: PropTypes.object
  };

  constructor(props) {
    super(props);
    this.state = {
      questionSetsQuery: null,
      questionSets: null,
      guideLevel: null,
      subject: null,
      queryDiffString: JSON.stringify(props.location.query),
      isReady: false,
      isLoading: false,
      duplicateQuestionSetIds: List()
    };
  }

  componentDidMount() {
    this.setInitialState();
  }

  componentDidUpdate() {
    const latestQueryDiffString = JSON.stringify(this.props.location.query);
    if (this.state.queryDiffString !== latestQueryDiffString) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState(
        {
          queryDiffString: latestQueryDiffString,
          isLoading: true
        },
        this.setInitialState
      );
    }
  }

  setInitialState = async () => {
    const userId = masqueradeStore.getUserIdByMasqueradeState();

    const subject = await getAuthoringSubjectBySlug(this.props.params.subjectSlug, userId);
    const guideLevel = await getAuthoringSubjectGuideLevelBySlug(
      subject.getId(),
      this.props.params.guideLevelSlug
    );
    const guideLevelId = guideLevel.getId();

    const questionSets = await getGuideLevelQuestionSets(guideLevelId);
    const duplicateQuestionSetIds = await getGuideLevelDuplicateSets(guideLevelId);

    this.setState({
      questionSetsQuery: getGuideLevelQuestionSetsQuery(guideLevelId),
      questionSets,
      guideLevel,
      subject,
      duplicateQuestionSetIds,
      isReady: true,
      isLoading: false
    });
  };

  render() {
    const {
      isReady,
      isLoading,
      questionSets,
      guideLevel,
      subject,
      questionSetsQuery,
      duplicateQuestionSetIds
    } = this.state;
    if (!isReady) {
      return <LoadingView />;
    }
    const questionSetMeta = questionSetsQuery.getResourceMetadata();
    const questionCount = questionSetMeta.getIn(['request', 'count_of_questions']);
    return (
      <div className='guide-level-practice-view'>
        <BootstrappedGuideLevelView
          {...this.props}
          isLoading={isLoading}
          questionSetsQuery={questionSetsQuery}
          questionSetsCount={questionSetMeta.getIn(['page', 'total_entries'], 0)}
          questionCount={questionCount}
          questionSets={questionSets}
          guideLevel={guideLevel}
          subject={subject}
          duplicateQuestionSetIds={duplicateQuestionSetIds}
        />
      </div>
    );
  }
}
